import Socket from '@/utils/ws'
import { getEnvImURL, isEnvTest } from '@/utils/env'
import { parseWsDataString } from '@/utils/message-helper'
import qs from 'qs'
import axios from '@/worker/axios'
import { Store } from '@/worker/store'
import {
  CODE_SEND_MESSAGE,
  CODE_FETCH_MESSAGE_ID,
  CODE_CREATE_CONVERSATION,
  CODE_RECEIVE_USER_INFO,
  ContentMessageToSend,
  Conversation
} from '@/utils/constant'
import { onConnect, broadcastMessage, getPorts as _getPorts, CustomMessagePort } from '@voghion/shared-worker/worker'

const SharedWorkerSupported = 'SharedWorkerGlobalScope' in self

interface MessagePort extends CustomMessagePort {
  visible?: boolean
}

const wsHost = getEnvImURL()

const doPostMessage = (info: any) => {
  if (SharedWorkerSupported) {
    broadcastMessage(info)
  } else {
    self.postMessage(info)
  }
}

const getPorts = () => {
  return _getPorts() as MessagePort[]
}

/**
 * 获取第一个页面可见的通道
 * @returns
 */
const getFirstVisiblePort = () => {
  let activePort: MessagePort | null = null
  const ports = getPorts()
  for (const port of ports) {
    if (port.visible) {
      activePort = port
      break
    }
  }
  return activePort || ports[0] || self
}

/**
 * 记录日志
 */
const logInfo = (event, params = {}) => {
  const _port = getFirstVisiblePort()
  _port &&
    _port.postMessage({
      type: 'logInfo',
      data: {
        event,
        params
      }
    })
}

/**
 * 记录错误
 */
const logError = (description, params = {}) => {
  const _port = getFirstVisiblePort()
  _port &&
    _port.postMessage({
      type: 'logError',
      data: {
        description,
        params
      }
    })
}

axios.errorLogger = (params) => {
  const _port = getFirstVisiblePort()
  _port &&
    _port.postMessage({
      type: 'logApiError',
      data: {
        params
      }
    })
}

const wsStore = new Store()
const wsModule = {
  socket: null,
  testFunc(data: any) {
    doPostMessage(data)
  },
  wsReceiveMessage(dataStr: string) {
    const receiveInfo = parseWsDataString(dataStr)
    const { code, data } = receiveInfo
    switch (code) {
      case CODE_RECEIVE_USER_INFO:
        // 收到 im 用户信息
        wsStore.imUserInfo = data
        break
      case CODE_FETCH_MESSAGE_ID:
        // 收到消息 id
        const { applyId } = data
        if (messagesPromiseModule[applyId]) {
          messagesPromiseModule[applyId].resolve(data)
        }
        break
      case CODE_CREATE_CONVERSATION:
        {
          /**
           * 创建会话成功
           * todo 目前只会和 vip 客服创建会话，所以头像和昵称先写死
           */
          const { conversationId, combineId, sessionId } = data
          const chat = wsStore.getChatById(conversationId)
          if (chat) {
            chat.sessionOpen = true
          } else {
            const newChat: Conversation = {
              conversationId,
              sessionId,
              headImage: wsStore.vipUserInfo?.headImage || '',
              nickname: wsStore.vipUserInfo?.nickname || '',
              latestContent: '',
              latestContentTranslate: '',
              latestTime: '',
              latestTimestamp: Date.now(),
              unreadCount: 0,
              sessionOpen: true
            }
            wsStore.addNewChat(newChat)
            if (messagesPromiseModule[`createroom_${combineId}`]) {
              messagesPromiseModule[`createroom_${combineId}`].resolve(data)
            }
          }
        }
        break
    }
    doPostMessage({ type: 'receive', data: receiveInfo })
  },
  wsSendMessage(sendStr: string) {
    if (this.socket && this.socket.readyState === 1) {
      this.socket.sendMsg(sendStr)
    } else {
      messagesQueue.push(sendStr)
    }
  },
  disconnect() {
    this.socket && this.socket.close()
  },
  wsConnect(data: any) {
    // 实例只创建一次
    if (!this.socket) {
      const url = `wss://${wsHost}/vog-im?${qs.stringify(data)}`
      const isTest = isEnvTest()
      const socket = new Socket({
        url: url,
        isHeart: true,
        isReconnection: true,
        timeout: isTest ? 20 * 1000 : 20 * 1000,
        connectTimeout: isTest ? 5 * 1000 : 30 * 1000,
        /**
         * 监听ws连接
         */
        openCallback() {
          if (wsModule.socket) {
            for (const sendStr of messagesQueue) {
              wsModule.socket.sendMsg(sendStr)
            }
            messagesQueue = []
          }
          doPostMessage({
            type: 'socketOpened'
          })
        },
        /**
         * 监听，处理接收数据正常处理
         * @param {*} data
         */
        receivedCallback(receiveStr) {
          // 监听服务器返回信息的方法
          wsModule.wsReceiveMessage(receiveStr)
        },
        /**
         * 监听关闭连接
         * @param {*} e
         */
        closeCallback(e) {
          const wsStatusInfo = JSON.stringify({
            wsStatus: socket.status,
            readyState: socket.readyState,
            isError: !!e.isError,
            willReconnect: !!e.willReconnect,
            reconnectionCount: e.reconnectionCount
          })
          if (e.isError && !e.willReconnect) {
            doPostMessage({ type: 'connectError', wsStatusInfo })
          }
          doPostMessage({
            type: 'socketClosed'
          })
        },
        /**
         * 监听连接报错
         * @param {*} e
         */
        errorCallback(e) {
          console.log('ws error', e)
        },
        logCallback(e) {
          if (e.type === 'info') {
            logInfo(e.event, e.params || {})
          } else if (e.type === 'error') {
            logError(e.description, e.params || {})
          }
        },
        readyStateChangeCallback() {
          const wsStatusInfo = JSON.stringify({ wsStatus: socket.status, readyState: socket.readyState })
          doPostMessage({ type: 'statusChange', wsStatusInfo })
        }
      })
      socket.connect()
      wsModule.socket = socket
    } else {
      wsModule.socket.reconnectionCount = 1
      wsModule.socket.url = `wss://${wsHost}/vog-im?${qs.stringify(data)}`
      if (wsModule.socket.status !== 'open') {
        wsModule.socket.connect()
      }
    }
  }
}

let messagesQueue = []
const messagesTimer = {}
const messagesPromiseModule = {}
/**
 * 获取新的消息 id
 * @param applyId
 * @returns
 */
const fetchMessageId = async (applyId: string) => {
  return new Promise<{
    messageId: number
  }>((resolve) => {
    wsModule.wsSendMessage(`${CODE_FETCH_MESSAGE_ID}:${JSON.stringify({ applyId })}`)
    messagesPromiseModule[applyId] = { resolve }
  }).finally(() => {
    messagesPromiseModule[applyId] = null
    delete messagesPromiseModule[applyId]
  })
}
/**
 * 更新消息
 * @param message
 * @param change
 */
const updateContentMessage = (message: ContentMessageToSend, change: Partial<ContentMessageToSend>) => {
  Object.assign(message, change)
  wsStore.addOrUpdateNewMessage(message)
}
/**
 * 创建会话
 */
const createConversation = async (conversationId?: number) => {
  const imUserInfo = wsStore.entryInfo
  const clientInfo = wsStore.clientInfo
  const toUserType = imUserInfo.toUserType || 'GROUP'
  const toUserRole = imUserInfo.toUserRole || 'GROUP_KEY_ACCOUNT_MANAGER_SALES_BEFORE'
  const toCombineId = imUserInfo.toCombineId || 1
  const params = {
    toCombineId,
    toUserType,
    toUserRole,
    country: clientInfo.country,
    currencyCode: clientInfo.currency,
    deviceCode: clientInfo.deviceCode,
    language: clientInfo.language,
    conversationId: null
  }
  if (conversationId && conversationId > 0) {
    params.conversationId = conversationId
  }
  const uniqueId = params.toCombineId
  return new Promise((resolve) => {
    wsModule.wsSendMessage(`${CODE_CREATE_CONVERSATION}:${JSON.stringify(params)}`)
    messagesPromiseModule[`createroom_${uniqueId}`] = { resolve }
  }).finally(() => {
    messagesPromiseModule[`createroom_${uniqueId}`] = null
    delete messagesPromiseModule[`createroom_${uniqueId}`]
  })
}
/**
 * 发送消息前，检查 session 是否过期
 * @param message
 * @returns
 */
const beforeSendMessage = async (message: ContentMessageToSend) => {
  const chat = wsStore.getChatById(message.conversationId)
  if (!chat || !chat.sessionOpen) {
    // 没有会话或者会话已过期
    await createConversation(message.conversationId)
  }
}
/**
 * 发送消息
 * @param message
 */
const sendContentMessage = async (message: ContentMessageToSend) => {
  if (messagesTimer[message.applyId]) {
    clearTimeout(messagesTimer[message.applyId])
  }
  /**
   * 超时处理
   */
  messagesTimer[message.applyId] = setTimeout(() => {
    updateContentMessage(message, {
      loading: false,
      failed: true
    })
  }, 20000)
  /**
   * 加入缓存
   */
  wsStore.addOrUpdateNewMessage(message)
  // if (!message.messageId) {
  //   const { messageId } = await fetchMessageId(message.applyId)
  //   wsStore.addOrUpdateNewMessage(message, {
  //     messageId
  //   })
  // }
  await beforeSendMessage(message)
  try {
    // 执行发送
    const { ackStatus } = await new Promise<{
      ackStatus: string
    }>((resolve, reject) => {
      wsModule.wsSendMessage(`${CODE_SEND_MESSAGE}:${JSON.stringify(message)}`)
      messagesPromiseModule[`${CODE_SEND_MESSAGE}_${message.messageId}`] = { resolve, reject }
    }).finally(() => {
      delete messagesPromiseModule[`${CODE_SEND_MESSAGE}_${message.messageId}`]
    })
    wsStore.addOrUpdateNewMessage(message, {
      loading: false,
      failed: ackStatus === 'SUCCESS'
    })
  } catch (errRes) {
    wsStore.addOrUpdateNewMessage(message, {
      loading: false,
      failed: true
    })
  }
  if (messagesTimer[message.applyId]) {
    clearTimeout(messagesTimer[message.applyId])
    delete messagesTimer[message.applyId]
  }
}

const start = (port) => {
  port.onmessage = (e) => {
    const { type, data, pageId } = e.data
    switch (type) {
      case 'init': {
        let autoConnect = false
        if (wsStore.clientInfo) {
          // 如果 token 变化，断开链接
          if (wsStore.clientInfo.userToken !== data.clientInfo.userToken) {
            wsStore.clear()
            wsModule.disconnect()
            autoConnect = !!data.clientInfo.userToken
          }
        }
        wsStore.clientInfo = data.clientInfo
        wsStore.entryInfo = data.entryInfo
        const socket = wsModule.socket
        port.postMessage({
          type: 'init',
          pageId,
          data: {
            wsStatus: socket ? socket.status : 'close',
            readyState: socket ? socket.readyState : 3,
            wsUserBaseInfo: wsStore.imUserInfo,
            autoConnect
          }
        })
        break
      }
      case 'test':
        wsModule.testFunc(data)
        break
      case 'connect':
        /**
         * 更新 userToken 到 store 里
         */
        if (data.vogToken && wsStore.clientInfo) {
          wsStore.clientInfo.userToken = data.vogToken
        }
        wsModule.wsConnect(data)
        break
      case 'msg':
        const { code, data: data1 } = parseWsDataString(data)
        if (code === 20000) {
          // 分发回页面
          doPostMessage({
            type: 'receive',
            data: {
              code,
              data: data1
            }
          })
        }
        wsModule.wsSendMessage(data)
        break
      case 'disconnect':
        wsModule.disconnect()
        break
      case 'logout':
        wsStore.clear()
        wsModule.disconnect()
        break
      case 'unload':
        port.onmessage = undefined
        break
      case 'visibilitychange':
        port.visible = data.visible
        break
      case 'getChatList':
        wsStore.getChatList(data.params).then((res) => {
          port.postMessage({
            type: 'getChatList',
            pageId,
            data: res
          })
        })
        break
      case 'getMessageList':
        wsStore.getMessageList(data.params).then((res) => {
          port.postMessage({
            type: 'getMessageList',
            pageId,
            data: res
          })
        })
        break
      case 'sendContentMessage':
        sendContentMessage(data)
        break
      case 'updateVipUserInfo':
        wsStore.vipUserInfo = data
        break
      case 'storeSync':
        doPostMessage({
          type: 'storeSync',
          data
        })
        break
      // 关闭连接，主要用来模拟异常场景
      case 'close':
        port.close()
        break
    }
  }
  const ports = getPorts()
  if (ports.length <= 1) {
    /**
     * worker 首次关联时触发 ready 事件，通知页面进行初始化的操作
     */
    port.postMessage({
      type: 'ready'
    })
  }
}

onConnect((e) => {
  let [port] = e.ports
  start(port)
})

/**
 * 在不支持 shared worker 的环境下，直接调用 start
 */
if (!('SharedWorkerGlobalScope' in self)) {
  start(self)
}
